import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Scale } from "lucide-react";
import { GoldenArc } from "../components/GoldenArc";
import { HERO_IMAGE, WHATSAPP_URL } from "../data/content";
import { scrollToId } from "../lib/scroll";

const HEADLINE = ["Segurança jurídica", "para relações que", "exigem confiança."];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yImage = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const yArc = useTransform(scrollYProgress, [0, 1], [0, -70]);

    return (
        <section
            id="inicio"
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden bg-graphite-dark"
        >
            <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-gold/10 blur-[140px]" />

            <div className="relative mx-auto grid min-h-screen max-w-7xl items-center gap-14 px-5 pb-28 pt-36 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:pt-40">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15, duration: 0.7 }}
                        className="mb-7 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.32em] text-gold-light"
                    >
                        <span className="hidden h-px w-10 shrink-0 bg-gold/70 sm:block" />
                        Câmara de Juízo Arbitral e Mediação Empresarial de São Paulo
                    </motion.p>

                    <h1 className="font-serif text-4xl leading-[1.08] text-cream sm:text-5xl lg:text-6xl">
                        {HEADLINE.map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-1.5">
                                <motion.span
                                    className="block"
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{
                                        delay: 0.28 + i * 0.15,
                                        duration: 0.95,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {i === 2 ? (
                                        <>
                                            {line.slice(0, -1)}
                                            <span className="text-gold-light">.</span>
                                        </>
                                    ) : (
                                        line
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.75, duration: 0.8 }}
                        className="mt-7 max-w-xl text-base leading-relaxed text-graphite-mute"
                    >
                        A CAJUSP administra procedimentos de arbitragem e mediação para a
                        resolução especializada de conflitos empresariais, contratuais e
                        patrimoniais — com independência, discrição e rigor técnico.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-cta"
                            className="inline-flex items-center gap-3 rounded-sm bg-gold px-7 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-graphite-deep transition-colors hover:bg-gold-light"
                        >
                            <MessageCircle size={16} strokeWidth={2.2} />
                            Falar com a CAJUSP
                        </a>
                        <button
                            type="button"
                            data-testid="hero-calculator-cta"
                            onClick={() => scrollToId("#calculadora")}
                            className="inline-flex items-center gap-3 rounded-sm border border-cream/25 px-7 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-cream transition-colors hover:border-gold hover:text-gold-light"
                        >
                            <Scale size={16} strokeWidth={2.2} />
                            Simular custas arbitrais
                        </button>
                    </motion.div>

                    <motion.button
                        type="button"
                        data-testid="hero-scroll-hint"
                        onClick={() => scrollToId("#instituicao")}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4, duration: 0.8 }}
                        className="mt-16 hidden items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-graphite-mute transition-colors hover:text-gold-light lg:inline-flex"
                        aria-label="Rolar para a seção A Instituição"
                    >
                        <ArrowDown size={14} className="animate-bounce" />
                        Conheça a instituição
                    </motion.button>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.55, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    className="relative hidden lg:block"
                >
                    <motion.div style={{ y: yImage }} className="relative">
                        <div className="relative overflow-hidden rounded-sm border border-gold/25">
                            <img
                                src={HERO_IMAGE.url}
                                alt={HERO_IMAGE.alt}
                                className="h-[520px] w-full object-cover"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-graphite-dark/70 via-transparent to-graphite-dark/20" />
                        </div>
                        <div className="absolute -left-6 bottom-10 max-w-xs border border-graphite-line bg-graphite-deep/90 px-5 py-4 backdrop-blur">
                            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold-light">
                                Lei nº 9.307/1996 · Art. 31
                            </p>
                            <p className="mt-1.5 font-serif text-lg leading-snug text-cream">
                                A sentença arbitral tem força de título executivo
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>

            <motion.div
                style={{ y: yArc }}
                className="pointer-events-none absolute inset-x-0 bottom-4"
            >
                <GoldenArc className="h-24 w-full opacity-60" />
            </motion.div>
        </section>
    );
};
